export type ApiErrorPayload =
  | { errors?: Array<{ message?: string }> }
  | { message?: string }
  | string
  | null;

export class ApiError extends Error {
  status: number;
  payload: ApiErrorPayload;

  constructor(status: number, message: string, payload: ApiErrorPayload) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

function normalizeBaseUrl(raw: string | undefined): string {
  const v = (raw || '').trim();
  return v.endsWith('/') ? v.slice(0, -1) : v;
}

export const API_BASE_URL = normalizeBaseUrl(import.meta.env.VITE_API_BASE_URL);

function resolveUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  if (!API_BASE_URL) return p;
  return `${API_BASE_URL}${p}`;
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit & { token?: string | null } = {}
): Promise<T> {
  const { token, headers, ...rest } = options;

  const res = await fetch(resolveUrl(path), {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers || {}),
    },
  });

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const payload = (isJson ? await res.json().catch(() => null) : await res.text().catch(() => null)) as ApiErrorPayload;

  if (!res.ok) {
    const validationMessage = (() => {
      if (!payload || typeof payload !== 'object') return null;
      if (!('errors' in payload)) return null;
      const errorsAny = (payload as any).errors;
      if (!errorsAny || typeof errorsAny !== 'object' || Array.isArray(errorsAny)) return null;

      const getFirstMsg = (v: any): string | null => {
        if (!v) return null;
        if (typeof v === 'string') return v;
        if (Array.isArray(v)) {
          const first = v[0];
          if (typeof first === 'string') return first;
          if (first && typeof first === 'object' && typeof first.message === 'string') return first.message;
        }
        if (typeof v === 'object' && typeof v.message === 'string') return v.message;
        return null;
      };

      for (const field of Object.keys(errorsAny)) {
        const msg = getFirstMsg(errorsAny[field]);
        if (msg) return msg;
      }

      return null;
    })();

    const msg =
      validationMessage ||
      (typeof payload === 'string' && payload) ||
      (payload && typeof payload === 'object' && 'message' in payload && typeof payload.message === 'string' && payload.message) ||
      (payload &&
        typeof payload === 'object' &&
        'errors' in payload &&
        Array.isArray((payload as any).errors) &&
        (payload as any).errors?.[0]?.message) ||
      `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, payload);
  }

  return payload as T;
}

export function safeJson(body: unknown): string {
  return JSON.stringify(body ?? {});
}

